import { initializeBrowserUserState } from "../browser-user-state.js";
import { browserLocalDataResetCoordinator } from "../local-data-reset.js";
import {
  accountPrimaryAreaUrls,
  accountRouteForPathname,
} from "../account/account-route-contract.js";
import {
  browserAccountStateSessionRepository,
  readBoundedAccountJson,
} from "../account/account-state-session-repository.js";

(async function () {
  function pageText(key, fallback) {
    const translated = window.__fishystuffLanguage?.t?.(key);
    return translated && translated !== key ? translated : fallback;
  }
  const accountSession = document.querySelector("fishy-account-session");

  function syncAccountRoute() {
    const accountRoute = accountRouteForPathname(window.location.pathname);
    const primaryAreaUrls = accountPrimaryAreaUrls(
      window.location.origin,
      window.location.pathname,
    );
    document.querySelectorAll("[data-account-primary-link]").forEach((link) => {
      const area = link.dataset.accountPrimaryLink;
      const selected = area === accountRoute?.area;
      link.href = primaryAreaUrls[area];
      link.classList.toggle("tab-active", selected);
      if (selected) link.setAttribute("aria-current", "page");
      else link.removeAttribute("aria-current");
    });
    document.querySelectorAll("[data-account-primary-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.accountPrimaryPanel !== accountRoute?.area;
    });
    if (accountSession) accountSession.active = accountRoute?.area === "identity";
  }

  document.addEventListener("click", (event) => {
    const link = event.target?.closest?.("[data-account-primary-link]");
    if (
      !link
      || event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;
    const url = new URL(link.href);
    if (url.origin !== window.location.origin || !accountRouteForPathname(url.pathname)) return;
    event.preventDefault();
    window.history.pushState({}, "", url.pathname);
    syncAccountRoute();
  });
  window.addEventListener("popstate", syncAccountRoute);
  syncAccountRoute();

  const sessionRepository = browserAccountStateSessionRepository();
  const telemetrySettings = [
    ...document.querySelectorAll("[data-account-telemetry-settings]"),
  ];
  if (telemetrySettings.length > 0) {
    const projectTelemetryAccess = (state) => {
      for (const settings of telemetrySettings) {
        settings.hidden = state.kind !== "ready";
      }
    };
    sessionRepository.subscribe(projectTelemetryAccess);
    projectTelemetryAccess(sessionRepository.state());
    void sessionRepository.load();
  }
  const administrationLink = document.querySelector("[data-account-administration-link]");
  if (administrationLink) {
    let accessRequest = null;
    let selectedAccountId = null;
    const repository = sessionRepository;
    repository.subscribe((state) => {
      if (state.kind !== "ready") {
        selectedAccountId = null;
        accessRequest?.abort();
        administrationLink.hidden = true;
        return;
      }
      if (state.accountId === selectedAccountId) return;
      selectedAccountId = state.accountId;
      accessRequest?.abort();
      const request = new AbortController();
      accessRequest = request;
      administrationLink.hidden = true;
      void (async () => {
        try {
          const response = await fetch(
            window.__fishystuffResolveApiUrl("/api/v1/account/admin-access"),
            {
              method: "GET", cache: "no-store", credentials: "include", redirect: "error",
              headers: { Accept: "application/json" }, signal: request.signal,
            },
          );
          if (!response.ok) return;
          const value = await readBoundedAccountJson(response, "Administrator access", 1024);
          if (
            !value || typeof value !== "object" || Array.isArray(value)
            || Object.keys(value).sort().join(",") !== "allowed,schemaVersion"
            || value.schemaVersion !== 1 || typeof value.allowed !== "boolean"
          ) return;
          if (!request.signal.aborted && selectedAccountId === state.accountId) {
            administrationLink.hidden = !value.allowed;
          }
        } catch (_error) {
          administrationLink.hidden = true;
        }
      })();
    });
    void repository.load();
  }


  const resetButtons = document.querySelectorAll("[data-reset-scope]");
  const runtimeResetButton = document.querySelector("[data-runtime-reset]");

  const resetCoordinator = browserLocalDataResetCoordinator();

  function showResetToast(tone, message, options) {
    const toast = window.__fishystuffToast;
    if (!toast || !message) return null;
    if (typeof toast[tone] === "function") return toast[tone](message, options || {});
    if (typeof toast.show === "function") {
      return toast.show({ tone, message, ...(options || {}) });
    }
    return null;
  }

  function bindConfirmedAction(button, action) {
    if (!button || typeof action !== "function") return;
    button.addEventListener("click", async () => {
      const confirmed = window.confirm(
        button.dataset.resetConfirm || "Clear local data on this browser?",
      );
      if (!confirmed) return;
      button.disabled = true;
      const progressToast = showResetToast(
        "info",
        button.dataset.resetProgress || "Clearing local data...",
        { duration: 0 },
      );
      try {
        await action(button.dataset.resetReload === "true");
        progressToast?.close?.();
        if (button.dataset.resetReload !== "true") {
          showResetToast("success", button.dataset.resetSuccess || "Local data cleared.");
        }
      } catch (_error) {
        progressToast?.close?.();
        showResetToast("error", "Reset is unavailable right now.");
      } finally {
        button.disabled = false;
      }
    });
  }

  resetButtons.forEach((button) => {
    const scope = button.dataset.resetScope;
    if (!scope) return;
    bindConfirmedAction(button, async (reload) => {
      await resetCoordinator.clearLocalDataScope(scope, { reload });
    });
  });
  bindConfirmedAction(runtimeResetButton, () => (
    resetCoordinator.clearMapRuntimePersistentState()
  ));

  await initializeBrowserUserState();
})();
